import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cn } from './cn';

const base = 'w-full rounded-[2px] border bg-white px-4 text-base text-black placeholder:text-ink/70 transition-colors focus:border-charcoal focus:outline-none';

type Common = { label: string; error?: string; className?: string };

function Shell({ label, error, className, children, id, required }: Common & { children: ReactNode; id: string; required?: boolean }) {
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.0625rem] text-ink">
        {label}{required && <span aria-hidden> *</span>}
      </label>
      {children}
      {error && <p role="alert" className="mt-1.5 text-sm text-red-700">{error}</p>}
    </div>
  );
}

export function TextField({ label, error, className, id, required, ...rest }: Common & InputHTMLAttributes<HTMLInputElement> & { id: string }) {
  return (
    <Shell label={label} error={error} className={className} id={id} required={required}>
      <input id={id} required={required} aria-invalid={!!error} className={cn(base, 'h-12', error ? 'border-red-600' : 'border-secondary-300')} {...rest} />
    </Shell>
  );
}

export function TextArea({ label, error, className, id, required, ...rest }: Common & TextareaHTMLAttributes<HTMLTextAreaElement> & { id: string }) {
  return (
    <Shell label={label} error={error} className={className} id={id} required={required}>
      <textarea id={id} required={required} aria-invalid={!!error} className={cn(base, 'min-h-36 resize-y py-3', error ? 'border-red-600' : 'border-secondary-300')} {...rest} />
    </Shell>
  );
}

export function SelectField({ label, error, className, id, required, children, ...rest }: Common & SelectHTMLAttributes<HTMLSelectElement> & { id: string }) {
  return (
    <Shell label={label} error={error} className={className} id={id} required={required}>
      <div className="relative">
        <select id={id} required={required} aria-invalid={!!error} className={cn(base, 'h-12 appearance-none pr-10', error ? 'border-red-600' : 'border-secondary-300')} {...rest}>{children}</select>
        <svg aria-hidden viewBox="0 0 12 8" className="pointer-events-none absolute right-4 top-1/2 h-2 w-3 -translate-y-1/2 text-ink" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M1 1.5l5 5 5-5" /></svg>
      </div>
    </Shell>
  );
}
